import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

const AudioPlayerContext = createContext();

export function AudioPlayerProvider({ children }) {
  const audioRef = useRef(null);
  const fallbackBlobUrlRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPlayingUrl, setCurrentPlayingUrl] = useState(null);

  const revokeFallbackBlobUrl = () => {
    if (fallbackBlobUrlRef.current) {
      URL.revokeObjectURL(fallbackBlobUrlRef.current);
      fallbackBlobUrlRef.current = null;
    }
  };

  const inferMimeTypeFromUrl = (url) => {
    const normalized = url.toLowerCase().split('?')[0];
    if (normalized.endsWith('.mp3')) return 'audio/mpeg';
    if (normalized.endsWith('.ogg')) return 'audio/ogg';
    if (normalized.endsWith('.m4a')) return 'audio/mp4';
    if (normalized.endsWith('.aac')) return 'audio/aac';
    return 'audio/wav';
  };

  const getAudioElement = () => {
    if (!audioRef.current) {
      const audio = document.createElement('audio');
      audio.preload = 'auto';
      audio.playsInline = true;
      audio.setAttribute('webkit-playsinline', 'true');
      audioRef.current = audio;

      audio.onplay = () => {
        setIsPlaying(true);
      };
      audio.onended = () => {
        setIsPlaying(false);
        setCurrentPlayingUrl(null);
      };
      audio.onerror = () => {
        console.error('Audio playback error');
        setIsPlaying(false);
        setCurrentPlayingUrl(null);
      };
      audio.onpause = () => {
        setIsPlaying(false);
      };
    }

    return audioRef.current;
  };

  const playAudio = (url) => {
    if (!url) {
      return Promise.resolve(false);
    }

    const audio = getAudioElement();
    revokeFallbackBlobUrl();
    audio.pause();
    audio.currentTime = 0;
    setCurrentPlayingUrl(url);
    audio.src = url;
    audio.load();

    try {
      const playPromise = audio.play();

      if (playPromise !== undefined) {
        return playPromise
          .then(() => {
            setIsPlaying(true);
            return true;
          })
          .catch(async (error) => {
            if (error?.name === 'NotSupportedError') {
              try {
                const response = await fetch(url, { cache: 'no-store' });
                if (!response.ok) {
                  throw new Error(`Audio fetch failed with status ${response.status}`);
                }

                const fetchedBlob = await response.blob();
                if (!fetchedBlob || fetchedBlob.size === 0) {
                  throw new Error('Fetched audio is empty');
                }

                const mimeType = fetchedBlob.type && fetchedBlob.type.startsWith('audio/')
                  ? fetchedBlob.type
                  : inferMimeTypeFromUrl(url);
                const normalizedBlob = new Blob([await fetchedBlob.arrayBuffer()], { type: mimeType });
                const blobUrl = URL.createObjectURL(normalizedBlob);
                fallbackBlobUrlRef.current = blobUrl;

                audio.pause();
                audio.currentTime = 0;
                audio.src = blobUrl;
                audio.load();
                await audio.play();
                setIsPlaying(true);
                setCurrentPlayingUrl(url);
                return true;
              } catch (fallbackError) {
                console.error('Audio fallback playback failed:', fallbackError);
              }
            }

            console.error('Audio playback failed:', error);
            setIsPlaying(false);
            setCurrentPlayingUrl(null);
            return false;
          });
      }

      setIsPlaying(true);
      return Promise.resolve(true);
    } catch (error) {
      console.error('Audio playback exception:', error);
      setIsPlaying(false);
      setCurrentPlayingUrl(null);
      return Promise.resolve(false);
    }
  };

  const stopAudio = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      revokeFallbackBlobUrl();
      setIsPlaying(false);
      setCurrentPlayingUrl(null);
    }
  };

  const isUrlPlaying = (url) => {
    return isPlaying && currentPlayingUrl === url;
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      revokeFallbackBlobUrl();
    };
  }, []);

  return (
    <AudioPlayerContext.Provider value={{ playAudio, stopAudio, isPlaying, isUrlPlaying, currentPlayingUrl }}>
      {children}
    </AudioPlayerContext.Provider>
  );
}

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
} 
